import gql from 'graphql-tag';
import { useMemo } from 'react';

import { useDownloadCSV } from '@/api/common';
import { InstantSearchSortOrder, type SolQueryParamsNew } from '@/components/InstantSearch';
import { csvDownloadRowLimit } from '@/constants';
import { useSolInfiniteQuery, useSolQuery } from '@/hooks/useSolQuery';

import { getVariablesFromTableParamsNew } from '../util/getVariablesFromTableParams';
import {
  type CompanyByIdGQLResponse,
  type CompanyKpiMetricsGQLResponse,
  type CompanyNameOptionsGQLResponse,
  type CompanyOptionsGQLResponse,
  type CompanyTrendVisitsGQLResponse,
  type WebTrafficActivityDetailSummaryGQLResponse,
  type WebTrafficActivityListGQLResponse,
} from './activityType';

const convertInTamOperator = (tableParams?: SolQueryParamsNew): SolQueryParamsNew | undefined => {
  if (tableParams?.filter == null) {
    return tableParams;
  }

  const hasInTam = tableParams.filter.some((filter) => filter.field === 'inTam');
  if (!hasInTam) {
    return tableParams;
  }

  return {
    ...tableParams,
    filter: tableParams.filter.map((filter) =>
      filter.field === 'inTam' ? { ...filter, operator: 'eq' as const } : filter,
    ),
  };
};

export const useWebTrafficActivityListCSVDownload = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
) => {
  const newTableParams = convertInTamOperator(tableParams);

  return useDownloadCSV({
    filename: 'C99_companyactivity',
    query: gql`
      query WebTrafficCompanyActivityList(
        $page: Pagination!
        $sort: [SortParamInput]!
        $filter: [FilterParamInput]!
        $startDate: DateTime!
        $endDate: DateTime!
        $searchQuery: String
      ) {
        visualization {
          companyActivity(
            page: $page
            sort: $sort
            filter: $filter
            startDate: $startDate
            endDate: $endDate
            searchQuery: $searchQuery
          ) {
            edges {
              node {
                Company: name
                Domain: tld
                inTam
                Region: region {
                  Name: name
                }
                Revenue_Range: revRange {
                  Name: name
                }
                Industry: sector {
                  Name: name
                }
                Metrics: metrics {
                  Impressions: impressions
                  Visits: visits
                  Last_Visit: lastActivityDate
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      ...getVariablesFromTableParamsNew(newTableParams),
      page: {
        offset: 0,
        limit: csvDownloadRowLimit,
      },
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
    },
  });
};

export const useWebTrafficActivityList = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;

  const newTableParams = convertInTamOperator(tableParams);

  const { data, error, isLoading } = useSolQuery<WebTrafficActivityListGQLResponse>(
    {
      query:
        !!canExecuteQuery &&
        gql`
          query WebTrafficCompanyActivityList(
            $page: Pagination!
            $sort: [SortParamInput]!
            $filter: [FilterParamInput]!
            $queryId: String
            $startDate: DateTime!
            $endDate: DateTime!
            $searchQuery: String
          ) {
            visualization {
              companyActivity(
                page: $page
                sort: $sort
                filter: $filter
                queryId: $queryId
                startDate: $startDate
                endDate: $endDate
                searchQuery: $searchQuery
              ) {
                edges {
                  node {
                    id
                    name
                    tld
                    inTam
                    metrics {
                      impressions
                      lastActivityDate
                      visits
                    }
                    region {
                      id
                      name
                    }
                    revRange {
                      id
                      name
                    }
                    sector {
                      id
                      name
                    }
                    audiences {
                      id
                      name
                      isTAM: is_default
                    }
                  }
                }
                queryId
                totalEdges
              }
            }
          }
        `,
      variables: {
        ...getVariablesFromTableParamsNew(newTableParams),
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    companies: data?.visualization.companyActivity.edges.map((edge) => edge.node),
    totalResults: data?.visualization.companyActivity.totalEdges,
    queryId: data?.visualization.companyActivity.queryId,
    isLoading,
    error,
  };
};

export const useCompanyNameOptions = ({
  search = '',
  pageSize = 50,
}: {
  search?: string;
  pageSize?: number;
}) => {
  const { data, isLoading, isFetchingNextPage, error, fetchNextPage } =
    useSolInfiniteQuery<CompanyNameOptionsGQLResponse>({
      queryKey: ['companyNameOptions', search],
      queryFn: ({ pageParam = 0, signal }) => {
        return {
          query: gql`
            query GetCompanyNameOptions(
              $page: Pagination!
              $sort: [SortParamInput]!
              $filter: [FilterParamInput]!
            ) {
              companies {
                get(page: $page, sort: $sort, filter: $filter) {
                  edges {
                    node {
                      id: companyId
                      name: companyName
                    }
                  }
                  pageMeta {
                    hasNext
                  }
                }
              }
            }
          `,
          variables: {
            ...getVariablesFromTableParamsNew({
              page: {
                offset: pageParam * pageSize,
                limit: pageSize,
              },
              sort: {
                direction: InstantSearchSortOrder.ASC,
                field: 'companyName',
              },
              filter: [
                search && search.length > 0
                  ? {
                      field: 'companyName',
                      operator: 'iLike',
                      operand: `${search}%`,
                    }
                  : {
                      field: 'companyName',
                      operator: 'isNotNull',
                    },
                {
                  field: 'companyName',
                  operator: 'ne',
                  booleanOperator: 'AND',
                  operand: ['-', '_'],
                },
              ],
            }),
          },
          signal,
        };
      },
      getNextPageParam: (lastPage, pages) => {
        return lastPage.companies.get.pageMeta.hasNext ? pages.length + 1 : undefined;
      },
    });

  const hasMore = data?.pages?.[data.pages.length - 1].companies.get.pageMeta.hasNext;

  const companyOptions = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.pages.map((d) => d.companies.get.edges.map((edge) => edge.node)).flat();
  }, [data]);

  return {
    companyOptions,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
  };
};

export const useCompanyOptions = ({
  search = '',
  pageSize = 50,
  startDate,
  endDate,
}: {
  search?: string;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
}) => {
  const { data, isLoading, isFetchingNextPage, error, fetchNextPage } =
    useSolInfiniteQuery<CompanyOptionsGQLResponse>({
      queryKey: ['companyOptions', search],
      queryFn: ({ pageParam = 0, signal }) => {
        return {
          query: gql`
            query GetCompanyOptions(
              $page: Pagination!
              $sort: [SortParamInput]!
              $filter: [FilterParamInput]!
              $startDate: DateTime!
              $endDate: DateTime!
              $searchQuery: String
            ) {
              visualization {
                companyActivity(
                  page: $page
                  sort: $sort
                  filter: $filter
                  startDate: $startDate
                  endDate: $endDate
                  searchQuery: $searchQuery
                ) {
                  edges {
                    node {
                      id
                      name
                      tld
                    }
                  }
                  pageMeta {
                    hasNext
                  }
                }
              }
            }
          `,
          variables: {
            ...getVariablesFromTableParamsNew({
              page: {
                offset: pageParam * pageSize,
                limit: pageSize,
              },
              sort: {
                direction: InstantSearchSortOrder.ASC,
                field: 'name',
              },
              filter: [],
              searchQuery: search,
            }),
            startDate: startDate + 'T00:00:00Z',
            endDate: endDate + 'T23:59:59Z',
          },
          signal,
        };
      },
      getNextPageParam: (lastPage, pages) => {
        return lastPage.visualization.companyActivity.pageMeta.hasNext
          ? pages.length + 1
          : undefined;
      },
    });

  const hasMore =
    data?.pages?.[data.pages.length - 1].visualization.companyActivity.pageMeta.hasNext;

  const companyOptions = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.pages
      .map((d) => d.visualization.companyActivity.edges.map((edge) => edge.node))
      .flat();
  }, [data]);

  return {
    companyOptions,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
  };
};

export const useCompanyTrendVisitsById = (
  companyId?: string,
  startDate?: string,
  endDate?: string,
) => {
  const canExecuteQuery = companyId && startDate?.length && endDate?.length;

  const { data, isLoading, error } = useSolQuery<CompanyTrendVisitsGQLResponse>({
    query:
      !!canExecuteQuery &&
      gql`
        query CompanyVisitTrend($startDate: DateTime!, $endDate: DateTime!, $companyId: String!) {
          visualization {
            companyVisitTrend(startDate: $startDate, endDate: $endDate, companyId: $companyId) {
              sundayOfWeek
              visits
            }
          }
        }
      `,
    variables: {
      companyId,
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
    },
  });

  return {
    companyTrendVisits: data?.visualization.companyVisitTrend,
    isLoading,
    error,
  };
};

export const useCompanySummaryById = (companyId?: string, startDate?: string, endDate?: string) => {
  const canExecuteQuery = companyId && startDate?.length && endDate?.length;

  const { data, isLoading, error } = useSolQuery<WebTrafficActivityDetailSummaryGQLResponse>({
    query:
      !!canExecuteQuery &&
      gql`
        query CompanyVisitSummary(
          $startDate: DateTime!
          $endDate: DateTime!
          $groupingField: TrafficVisitBreakdownGrouping!
          $companyId: String!
          $intentPage: Pagination!
          $intentSort: [SortParamInput]!
          $intentFilter: [FilterParamInput]!
        ) {
          visualization {
            trafficVisitBreakdown(
              startDate: $startDate
              endDate: $endDate
              groupingField: $groupingField
              companyId: $companyId
            ) {
              count
              percentOfTotal
              total
              vendor {
                channel {
                  id
                  name
                  color
                }
                id
                name
              }
            }
            companySummary(startDate: $startDate, endDate: $endDate, companyId: $companyId) {
              audiences {
                id
                name
                isTAM: is_default
              }
              keywords {
                id
                name
              }
              opportunities {
                id
                name
                isRecentDeal
              }
              metrics {
                lastActivityDate
              }
              webActivityPages
            }
            companyIntent(
              startDate: $startDate
              endDate: $endDate
              companyId: $companyId
              page: $intentPage
              sort: $intentSort
              filter: $intentFilter
            ) {
              edges {
                node {
                  id
                  provider: intentProvider {
                    id
                    name
                  }
                  type: intentType
                  name: intentName
                  count: countIntent
                }
              }
              totalEdges
            }
          }
        }
      `,
    variables: {
      companyId,
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      groupingField: 'vendor',
      intentPage: {
        offset: 0,
        limit: 20,
      },
      intentSort: [
        {
          direction: InstantSearchSortOrder.DESC,
          field: 'countIntent',
        },
      ],
      intentFilter: [],
    },
  });

  const dataWithVendorDisplayName = useMemo(() => {
    if (!data) {
      return data;
    }

    const updatedTrafficVisitBreakdown = data.visualization.trafficVisitBreakdown.map((d) => {
      const displayName = d.vendor.channel?.name
        ? `${d.vendor.name} - ${d.vendor.channel.name}`
        : d.vendor.name;

      return {
        ...d,
        vendor: {
          ...d.vendor,
          displayName,
        },
      };
    });

    return {
      companySummary: {
        ...data.visualization.companySummary,
        intents: data.visualization.companyIntent.edges.map((edge) => edge.node),
        totalIntents: data.visualization.companyIntent.totalEdges,
      },
      trafficVisitBreakdown: updatedTrafficVisitBreakdown.sort((a, b) => b.count - a.count),
    };
  }, [data]);

  return {
    companySummary: dataWithVendorDisplayName,
    isLoading,
    error,
  };
};

export const useCompanyById = (companyId?: string) => {
  const { data, isLoading, error } = useSolQuery<CompanyByIdGQLResponse>({
    query: gql`
      query CompanyById($id: String!) {
        companies {
          getById(id: $id) {
            account {
              name
            }
            id
            name
            tld
          }
        }
      }
    `,
    variables: {
      id: companyId,
    },
  });

  return {
    company: data?.companies.getById,
    isLoading,
    error,
  };
};

export const useCompanyKpiMetrics = (companyId?: string, startDate?: string, endDate?: string) => {
  const canExecuteQuery = !!companyId && !!startDate && !!endDate;

  const { data, isLoading, error } = useSolQuery<CompanyKpiMetricsGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query CompanyKpiMetrics($startDate: DateTime!, $endDate: DateTime!, $companyIds: [String]) {
          visualization {
            trafficKpi2(startDate: $startDate, endDate: $endDate, companyIds: $companyIds) {
              closedWonOpportunities
              opportunityCountNonDigitalEvents
              pipelineOpportunities
              visits
              vendorCount
            }
          }
        }
      `,
    variables: {
      companyIds: companyId ? [companyId] : undefined,
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
    },
  });

  return {
    companyKpiMetrics: data?.visualization.trafficKpi2,
    isLoading,
    error,
  };
};
